// @ts-strict-ignore

import { Injectable } from '@angular/core';
import { createSelector, Store } from '@ngrx/store';
import {
  IAmountInfo,
  IChargeInfo,
  ICondition,
  IEquipment,
  IPriceInfo,
  ITechnicalData,
  IVehicle,
  IVehicleConfiguration
} from 'fcis-widget/dist/types/interfaces/ICalculationModel';
import { IFcisWidgetParameters } from 'fcis-widget/dist/types/interfaces/IInitializationConfig';
import { first, from, iif, map, mergeMap, Observable, of, reduce, switchMap } from 'rxjs';

import type { Calculations$Params } from 'src/app/api/ocapi/fn/calculations/calculations.ts';
import {
  AmountInfo,
  CalcResponse,
  CalculationModel,
  ChargeInfo,
  Condition,
  Equipment,
  EquipmentPriceInfo,
  FinancialParameters,
  PriceInfoContainer,
  TechnicalData,
  VehicleConfiguration
} from 'src/app/api/ocapi/models';
import { CalculationsService } from 'src/app/api/ocapi/services';
import { Prices, SelectablesResponseVehicleComponent, Vehicle, VehicleComponent } from 'src/app/api/owcc/models';
import { selectApiContext } from 'src/app/select-api-context';
import { fromCarl, selectUsePriceDisplayComponent } from 'src/app/store/common/common.selectors';
import { seamlessStoreFns } from 'src/app/store/common/common.utils';
import { selectOnlineCodeLanguageAndCountry } from 'src/app/store/runtime-variables/runtime-variables.selectors';
import { BusinessType } from 'src/app/utils/global-constants';
import { DiscountId } from 'src/app/utils/models/price-information';
import { SelectablesResponseVehicleComponentCollection } from 'src/app/utils/models/selectables-response-vehicle-component-collection.interface';
import { VehicleComponentCollection } from 'src/app/utils/models/vehicle-component-collection.interface';

import { Vehicle as OcapiVehicle } from '../../api/ocapi/models/vehicle';

import { OptionalOnlineOrFinancialCode } from './finance-calculator/finance-calculator.component';
import {
  FcisCustomerTypeActivation,
  FinanceConfig,
  FinanceRate,
  FinanceRequestAdditionalValuesType,
  FinanceRequestPriceId
} from './finance.models';

export const OWCC_CALLER = 'cc';

const BRAND_MERCEDES_BENZ = 'mercedes-benz';
const DIVISION_PASSENGER_CARS = 'pc';

const extractCodeAndType = (id = ''): { code: string; codeType: string } => {
  const splitId = id.split('-'); // e.g. AU-001 (usual format) or PS-012# or LE-L (exceptional formats)

  return {
    codeType: splitId[0],
    code: splitId[1] // PPM Standard must be used (3-digit code). NOTE: THIS IS NOT ALWAYS THE CASE
  };
};

const getEquipmentOrUndefined = (
  value: SelectablesResponseVehicleComponent | VehicleComponent,
  key: string,
  showZeros = false
): Equipment | undefined => {
  const { price, name } = value;
  const hasPrice = price.netPrice !== 0;
  if (hasPrice || showZeros) {
    const netPrice: EquipmentPriceInfo = {
      id: FinanceRequestPriceId.NetPrice,
      currency: price.currencyISO,
      rawValue: price.netPrice,
      charges: []
    };
    const grossPrice: EquipmentPriceInfo = {
      id: FinanceRequestPriceId.GrossPrice,
      currency: price.currencyISO,
      rawValue: price.price,
      charges: []
    };
    const { code, codeType } = extractCodeAndType(key);
    return {
      code,
      codeType,
      name,
      prices: [netPrice, grossPrice]
    };
  }
  return undefined; // intentionally
};

const getEquipments = (
  obj: Record<string, SelectablesResponseVehicleComponent | VehicleComponent>,
  filterZeros = false
): Equipment[] =>
  Object.entries(obj || {}).reduce<Equipment[]>((array, equipment) => {
    const [key, value] = equipment;
    return [...array, getEquipmentOrUndefined(value, key, filterZeros)].filter(Boolean);
  }, []);

const buildEquipments = (collection: SelectablesResponseVehicleComponentCollection, filterZeros = false): Equipment[] =>
  getEquipments(collection, filterZeros);

const OCAPI_INFORMATION_KEYS = [
  'fuelType',
  'emissionStandard',
  'combustionPowerKw',
  'electricPowerKw',
  'co2EmissionWltp',
  'electricRangeWltp',
  'electricConsumptionWltp',
  'maxWeight',
  'seats'
];

const enum MindSettingsForLeasingRatesOnMotoTiles {
  IgnoreMotoTileSettings = 'ignoreMotoTileSettings',
  MindMotoTileSettings = 'mindMotoTileSettings'
}

@Injectable({
  providedIn: 'root'
})
export class FinanceService {
  public constructor(
    private readonly calculationsService: CalculationsService,
    private readonly store: Store
  ) {}

  public getParameter$(fcisVehicleObj: IVehicle, productCode: string): Observable<IFcisWidgetParameters> {
    return this.store.select(
      createSelector(
        selectApiContext,
        selectUsePriceDisplayComponent,
        ({ country, market }, usePriceDisplayComponent) => ({
          caller: OWCC_CALLER,
          // market in FCIS is equivalent to country in OWCC
          market: country,
          // locale in FICS is equivalent to market in OWCC
          locale: market, // e.g. de_AT
          productCode: usePriceDisplayComponent ? productCode : undefined,
          vehicle: fcisVehicleObj
        })
      )
    );
  }

  public isFcisEnabledForCurrentCustomerType(): Observable<boolean> {
    return this.store
      .select(fromCarl.selectConfig<boolean | FcisCustomerTypeActivation>('financial.useFcis', false))
      .pipe(
        switchMap(fcisActivation =>
          seamlessStoreFns.observeBusinessTypeInSeamlessStore().pipe(
            map((businessType = BusinessType.B2C) => {
              // for backwards compatibility
              if (typeof fcisActivation === 'boolean') {
                return fcisActivation === true && businessType === BusinessType.B2C;
              }

              switch (businessType) {
                case BusinessType.B2C: {
                  return fcisActivation.b2c;
                }
                case BusinessType.B2B: {
                  return fcisActivation.b2b;
                }
                case BusinessType.B2E: {
                  return fcisActivation.b2e;
                }
                default: {
                  return false;
                }
              }
            })
          )
        )
      );
  }

  public getFinanceRatesForMotorizations(
    vehicles: Vehicle[],
    input: FinancialParameters[],
    opaque: string
  ): Observable<FinanceRate[]> {
    const onShowRates$ = from(vehicles).pipe(
      mergeMap(vehicle =>
        this.getFinanceRates(vehicle, input, opaque).pipe(
          map(
            (result: CalcResponse): FinanceRate => ({
              baumuster: vehicle.baumuster,
              rate: result?.output?.rate
            })
          )
        )
      ),
      reduce<FinanceRate, FinanceRate[]>((acc, curr) => [...acc, curr], [])
    );

    return this.getShowMotorizationRatesMarketConfig<FinanceRate[]>(showRates =>
      showRates ? onShowRates$ : of([] as FinanceRate[])
    );
  }

  public getFinanceRates(
    vehicle: Vehicle,
    input: FinancialParameters[],
    apiOpaque: string,
    availableComponents?: SelectablesResponseVehicleComponentCollection,
    selectedComponents?: SelectablesResponseVehicleComponentCollection,
    filterZeros = false
  ): Observable<CalcResponse> {
    return this.store.select(selectOnlineCodeLanguageAndCountry).pipe(
      first(),
      switchMap(({ onlineCode, country, language }) => {
        const body: CalculationModel = this.createFinanceRequestCalculationModel(
          vehicle,
          input,
          apiOpaque,
          buildEquipments(availableComponents, filterZeros),
          buildEquipments(selectedComponents, filterZeros), // for the motorization requests we intentionally leave selected components empty
          onlineCode
        );

        return this.calculationsService.calculations({
          market: country.toLowerCase() as Calculations$Params['market'],
          'Accept-Language': `${language}-${country}`, // which is _not_ the same as we would get from the api context (different separator)!
          body
        });
      })
    );
  }

  public getFinanceRatesForConflicts(
    vehicle: Vehicle,
    opaque: string,
    input: FinancialParameters[],
    components: SelectablesResponseVehicleComponentCollection,
    selectedComponents: SelectablesResponseVehicleComponentCollection
  ): Observable<CalcResponse> {
    const onShowRates$ = this.getFinanceRates(vehicle, input, opaque, components, selectedComponents, true);

    return this.getShowMotorizationRatesMarketConfig<CalcResponse>(
      showRates => (showRates ? onShowRates$ : of()),
      MindSettingsForLeasingRatesOnMotoTiles.IgnoreMotoTileSettings
    );
  }

  /*
   * Object structure see
   * - https://confluence.mercedes-benz.io/display/FINANCCALC/Cleopatra%3A+Installation+guide
   * - https://git.i.mercedes-benz.com/dmo-apis/OneCalculationAPI
   * Table: Cars BTO (Build to order)
   */
  public buildVehicleObject(
    vehicle: Vehicle,
    selectableComponents: VehicleComponentCollection | undefined,
    selectedComponents: (VehicleComponent | SelectablesResponseVehicleComponent)[],
    onlineOrFinancialCode: OptionalOnlineOrFinancialCode
  ): IVehicle {
    /* see https://git.i.mercedes-benz.com/dmo-apis/OneCalculationAPI/issues/143 */
    const buildSelectedEquipments = (
      components: (VehicleComponent | SelectablesResponseVehicleComponent)[]
    ): IEquipment[] =>
      (components || []).map(component => {
        const { code, codeType } = extractCodeAndType(component.id);

        return {
          code,
          codeType,
          name: component.name,
          prices: [
            {
              id: FinanceRequestPriceId.NetPrice,
              currency: component.price?.currencyISO || '',
              rawValue: component.price?.netPrice,
              charges: [
                /* intentionally empty */
              ]
            },
            {
              id: FinanceRequestPriceId.GrossPrice,
              currency: component.price?.currencyISO || '',
              rawValue: component.price?.price,
              charges: [
                /* intentionally empty */
              ]
            }
          ]
        };
      });

    const buildPrices = (veh: Vehicle): IPriceInfo[] => {
      const { currencyISO, basePrice, configurationPrice } = veh.priceInformation || {};
      return [
        {
          id: FinanceRequestPriceId.BaseListPrice,
          currency: currencyISO || '',
          rawValue: this.getFcisInfos(basePrice)?.price ?? Number.NaN,
          charges: this.getCharges<IChargeInfo>(this.getFcisInfos(basePrice))
        },
        {
          id: FinanceRequestPriceId.GrossListPrice,
          currency: currencyISO || '',
          rawValue: this.getFcisInfos(configurationPrice)?.price ?? Number.NaN,
          charges: this.getCharges<ChargeInfo>(this.getFcisInfos(configurationPrice))
        }
      ];
    };

    return {
      technicalIdReference: undefined, // intentionally undefined
      fin: undefined, // intentionally undefined
      vehicleConfiguration: this.buildVehicleConfiguration(vehicle, buildSelectedEquipments(selectedComponents)),
      // Temporarily added cast to prevent error 'VehicleComponent' is not assignable to type 'SelectablesResponseVehicleComponent'.
      // Follow up ticket: https://jira.mercedes-benz.io/browse/MBCC-791
      alternativeConfiguration: buildEquipments(selectableComponents as SelectablesResponseVehicleComponentCollection), // to get partial rates
      condition: this.buildCondition() as ICondition,
      technicalData: this.buildTechnicalData<ITechnicalData>(vehicle),
      name: vehicle.name,
      prices: buildPrices(vehicle),
      additionalValues: this.getAdditionalValues<IAmountInfo>(vehicle.priceInformation?.discountedPrice),
      // intentionally online code as financial code. fallback to 'null' is _mandatory_ to reset online codes if necessary! 'undefined' not working!
      financialCode: onlineOrFinancialCode || null,
      usage: undefined // intentionally undefined
    };
  }

  private buildTechnicalData<T extends TechnicalData | ITechnicalData>(vehicle: Vehicle): T[] {
    return OCAPI_INFORMATION_KEYS.map(key => ({
      id: key,
      value: vehicle.ocapiInformation?.technicalValues?.find(v => v?.id === key)?.rawValue ?? ''
    })) as T[];
  }

  private buildCondition(): ICondition | Condition {
    return {
      firstRegistrationDate: undefined,
      // intentionally no milage (optional)
      condition: 'new'
    };
  }

  private buildVehicleConfiguration(
    vehicle: Vehicle,
    equipments: Equipment[] | IEquipment[]
  ): IVehicleConfiguration | VehicleConfiguration {
    return {
      brand: BRAND_MERCEDES_BENZ,
      division: DIVISION_PASSENGER_CARS,
      baumuster: vehicle.baumuster,
      nst: vehicle.nst || null,
      modelYear: vehicle.modelYear,
      changeYear: vehicle.changeYear,
      retailerId: undefined,
      salesDescription: undefined,
      equipments
    };
  }

  private getFcisInfos(priceObject: Prices): Prices {
    return priceObject.fcis || priceObject;
  }

  private getAdditionalValues<T extends IAmountInfo | AmountInfo>(discountedPrice?: Prices | null): T[] {
    const getDiscountValues = (discountId: DiscountId, disPrice?: Prices): number => {
      const resolvedDiscount = disPrice?.discounts
        ?.filter(discount => (discount.id as DiscountId) === discountId)
        ?.pop();

      return resolvedDiscount?.bonus || 0;
    };

    return [
      {
        rawAmount: getDiscountValues(DiscountId.ManufacturerDiscountEnvironmentalBonus, discountedPrice),
        type: FinanceRequestAdditionalValuesType.EnvironmentalBonus
      } as T
    ];
  }

  /**
   * Returns the charges / taxes for the given vehicle price.
   * Note: charges currently implemented especially for NL tax 'BPM'.
   * But: implementation is generic.
   * As soon as the backend enables more taxes (for more markets) this implementation should also work.
   * For the 'type' we currently don't need any mapping, lower case is sufficient.
   * More details see here: https://git.i.mercedes-benz.com/pages/dmo-apis/OneCalculationAPI/api-definition/#chargeinfo
   */
  private getCharges<T extends IChargeInfo | ChargeInfo>({ taxes }: Prices): T[] {
    const charges: T[] = [];
    if (taxes) {
      for (const tax of taxes) {
        charges.push({
          label: tax.rate,
          type: tax.id,
          rawAmount: tax.value
        } as T);
      }
    }
    return charges;
  }

  private createFinanceRequestCalculationModel(
    vehicle: Vehicle,
    input: FinancialParameters[],
    opaque: string,
    availableEquipments: Equipment[],
    selectedEquipments: Equipment[],
    onlineOrFinacialCode: OptionalOnlineOrFinancialCode
  ): CalculationModel {
    const { currencyISO, basePrice, configurationPrice, discountedPrice } = vehicle.priceInformation || {};
    const prices: PriceInfoContainer = [
      {
        id: FinanceRequestPriceId.BaseListPrice,
        currency: currencyISO || '',
        rawValue: this.getFcisInfos(basePrice)?.price ?? Number.NaN,
        charges: this.getCharges<ChargeInfo>(this.getFcisInfos(basePrice))
      },
      {
        id: FinanceRequestPriceId.GrossListPrice,
        currency: currencyISO || '',
        rawValue: this.getFcisInfos(configurationPrice)?.price ?? Number.NaN,
        charges: this.getCharges<ChargeInfo>(this.getFcisInfos(configurationPrice))
      }
    ];

    const ocapiVehicle: OcapiVehicle = {
      additionalValues: this.getAdditionalValues<AmountInfo>(discountedPrice),
      alternativeConfiguration: availableEquipments,
      vehicleConfiguration: this.buildVehicleConfiguration(vehicle, selectedEquipments) as VehicleConfiguration,
      condition: this.buildCondition() as Condition,
      technicalData: this.buildTechnicalData<TechnicalData>(vehicle),
      name: vehicle?.name,
      prices,
      financialCode: onlineOrFinacialCode || null
    };

    return {
      vehicle: ocapiVehicle,
      input,
      opaque
    };
  }

  private getShowMotorizationRatesMarketConfig<T>(
    callback: (showRates: boolean) => Observable<T>,
    mindLeasingRateSettingsForMotoTiles: MindSettingsForLeasingRatesOnMotoTiles = MindSettingsForLeasingRatesOnMotoTiles.MindMotoTileSettings
  ): Observable<T> {
    return this.store.pipe(
      fromCarl.selectConfigOnce('financial', { showRates: false, preventRatesForMotorization: true }),
      switchMap((financialConfig: FinanceConfig) =>
        iif(
          () =>
            financialConfig.showRates &&
            (mindLeasingRateSettingsForMotoTiles === MindSettingsForLeasingRatesOnMotoTiles.IgnoreMotoTileSettings ||
              !financialConfig.preventRatesForMotorization),
          callback(true),
          callback(false)
        )
      )
    );
  }
}
